export type JobCommissionPart = {
  amount?: number;
  percentage?: number;
};

export type JobCommissions = {
  clientCharge?: JobCommissionPart;
  workerShare?: JobCommissionPart;
  tax?: JobCommissionPart;
  clientDiscount?: JobCommissionPart;
  workerDiscount?: JobCommissionPart;
};

type JobLike = {
  budget?: number;
  commissions?: JobCommissions;
  clientVoucherCode?: string | null;
  providerVoucherCode?: string | null;
} | null | undefined;

const DEFAULT_CLIENT_PCT = Number(import.meta.env.VITE_CLIENT_COMMISSION ?? 17);
const DEFAULT_TAX_PCT = Number(import.meta.env.VITE_TAX_COMMISSION ?? 11.5);
const DEFAULT_WORKER_PCT = Number(import.meta.env.VITE_PROVIDER_COMMISSION ?? 8);

function partAmount(part?: JobCommissionPart): number {
  return part?.amount != null ? Number(part.amount) : 0;
}

function partPercentage(part?: JobCommissionPart): number {
  return part?.percentage != null ? Number(part.percentage) : 0;
}

/**
 * Resolve commission % and $ from job.commissions (env defaults if missing).
 *
 * Semantics: clientCharge / workerShare are the full (pre-discount) fees.
 * clientDiscount / workerDiscount are applied on top; actual charged fee =
 * fullFee - discountAmount.
 */
export function getJobCommissions(job: JobLike) {
  const budget = Number(job?.budget || 0);
  const c = job?.commissions;

  const clientPercentage = Number(c?.clientCharge?.percentage ?? DEFAULT_CLIENT_PCT);
  const taxPercentage = Number(c?.tax?.percentage ?? DEFAULT_TAX_PCT);
  const workerPercentage = Number(c?.workerShare?.percentage ?? DEFAULT_WORKER_PCT);

  const clientAmount =
    c?.clientCharge?.amount != null
      ? Number(c.clientCharge.amount)
      : budget * (clientPercentage / 100);
  const taxAmount =
    c?.tax?.amount != null ? Number(c.tax.amount) : budget * (taxPercentage / 100);
  const workerAmount =
    c?.workerShare?.amount != null
      ? Number(c.workerShare.amount)
      : budget * (workerPercentage / 100);

  const clientDiscountAmount = partAmount(c?.clientDiscount);
  const clientDiscountPercentage = partPercentage(c?.clientDiscount);
  const workerDiscountAmount = partAmount(c?.workerDiscount);
  const workerDiscountPercentage = partPercentage(c?.workerDiscount);

  const hasClientDiscount = clientDiscountAmount > 0 || clientDiscountPercentage > 0;
  const hasWorkerDiscount = workerDiscountAmount > 0 || workerDiscountPercentage > 0;

  // Full fee is stored on clientCharge / workerShare; discount is separate.
  const clientChargeBeforeDiscount = clientAmount;
  const workerShareBeforeDiscount = workerAmount;
  const clientChargeAfterDiscount = Math.max(0, clientAmount - clientDiscountAmount);
  const workerShareAfterDiscount = Math.max(0, workerAmount - workerDiscountAmount);

  const clientChargeAfterPercentage =
    budget > 0 ? (clientChargeAfterDiscount / budget) * 100 : 0;
  const workerShareAfterPercentage =
    budget > 0 ? (workerShareAfterDiscount / budget) * 100 : 0;

  const clientVoucherCode =
    typeof job?.clientVoucherCode === 'string' && job.clientVoucherCode.trim()
      ? job.clientVoucherCode.trim()
      : null;
  const providerVoucherCode =
    typeof job?.providerVoucherCode === 'string' && job.providerVoucherCode.trim()
      ? job.providerVoucherCode.trim()
      : null;

  return {
    clientPercentage,
    taxPercentage,
    workerPercentage,
    clientAmount,
    taxAmount,
    workerAmount,
    // Totals use the fee actually charged after voucher discount.
    totalClientCharge: budget + clientChargeAfterDiscount + taxAmount,
    workerNet: budget - workerShareAfterDiscount,
    clientDiscountAmount,
    clientDiscountPercentage,
    workerDiscountAmount,
    workerDiscountPercentage,
    hasClientDiscount,
    hasWorkerDiscount,
    clientChargeBeforeDiscount,
    workerShareBeforeDiscount,
    clientChargeAfterDiscount,
    workerShareAfterDiscount,
    clientChargeAfterPercentage,
    workerShareAfterPercentage,
    clientVoucherCode,
    providerVoucherCode,
  };
}
